import { addressSchema } from '@/common/schemas/address.schema';
import { nullableOptionSchema, optionSchema } from '@/common/schemas/option.schema';
import { DocumentData } from '@/common/types/document';
import { ProductData } from '@/common/types/product.type';
import { ProductCategoryData } from '@/common/types/productCategory.type';
import { unFormatCurrency, unFormatUnknownUnit } from '@/common/utils/unFormatters';
import { z } from 'zod';

const geosSchema = z.object({
  value: z.string().optional(),
  type: z.string().optional(),
  label: z.custom<JSX.Element | undefined>().optional().nullable(),
});

export const updateSchema = z.object({
  commercial_manager: optionSchema.optional(),
  commercial_manager_substitute: nullableOptionSchema.optional().nullable(),
  bidding_information: z.object({
    contest_date: z.string().optional().nullable(),
    geo_geoables: z.array(geosSchema).optional().nullable(),
    government_segment: optionSchema.optional(),
    minimum_establishments_in_region: z.string().optional().nullable(),
    notice_number: z.string().optional().nullable(),
    object_of_the_bidding: z.string().optional().nullable(),
    estimated_equipment_total: z.string().optional().nullable(),
  }).optional().nullable(),
  fee_rule: z.object({
    special_entries: z.array(
      z.object({
        id: z.string().optional().nullable(),
        target: optionSchema.optional().nullable(),
        fee_template_id: z.string().optional().nullable(),
        value: z.string().optional().nullable(),
      }).optional().nullable(),
    ).optional().nullable(),
  }).optional().nullable(),
  fiscal_document_information: z.object({
    consumption_observations: z.string().optional().nullable(),
    service_observations: z.string().optional().nullable(),
    seller_cnpj: z.string().optional().nullable(),
    fiscal_document_kind: optionSchema.optional(),
  }).optional().nullable(),
  general_information: z.object({
    cnpj: z.string().optional().nullable(),
    email: z.string().optional().nullable(),
    fantasy_name: z.string().optional().nullable(),
    official_name: z.string().optional().nullable(),
    primary_cnae: z.string().optional().nullable(),
    state_inscription: z.string().optional().nullable(),
  }).optional().nullable(),
  address_attributes: addressSchema.optional(),
  contact_for_master_user: z.object({
    value: z.string().optional().nullable(),
  }).optional().nullable(),
  printer_machine_id: optionSchema.optional().nullable(),
  observations: z.string().optional().nullable()
});

export type PublicCompanyLeadsFunnelUpdateValidationSchema = z.infer<typeof updateSchema>;

export const updateContactSchema = z.object({
  contacts: z.object({
    email: z.string().optional().nullable(),
    full_name: z.string().optional().nullable(),
    role_in_company: z.string().optional().nullable(),
    phone: z.object({
      number: z.string().optional().nullable(),
      kind: z.string().optional().nullable(),
      whatsapp_capability: z.string().optional().nullable(),
    }).optional(),
    cpf: z.string().optional().nullable(),
    documents: z.array(
      z.object({
        id: z.string().optional().nullable(),
        cnh: z.string().optional().nullable(),
        file: z.custom<Blob>().optional().nullable(),
        issued_on_date: z.string().optional().nullable(),
        expires_at: z.string().optional().nullable(),
        rg: z.string().optional().nullable(),
        type: z.string().optional().nullable(),
        last_review: z.object({
          description: z.string().optional().nullable(),
          state: z.string().optional().nullable(),
        }).optional().nullable(),
        doc_options: z.custom<DocumentData>().optional().nullable(),
        checked: z.boolean().optional().nullable(),
      }).optional(),
    ),
  }),
});

export type PublicCompanyLeadsFunnelUpdateContactValidationSchema = z.infer<typeof updateContactSchema>;

export const contractProductPriceSchema = z.object({
  id: z.string().optional().nullable(),
  value: z.string().optional().nullable(),
  geo: geosSchema.optional().nullable(),
});

export type PublicCompanyLeadsFunnelUpdateContractProductPriceValidationSchema = z.infer<typeof contractProductPriceSchema>;

export const contractProductSchema = z.object({
  id: z.string().optional(),
  product: z.custom<ProductData>(),
  global_value_in_unit_for_consumption: z.string(),
  global_value_in_currency: z.string(),
  price_rule: optionSchema.optional(),
  contract_product_category_product_prices: z.array(contractProductPriceSchema).optional().nullable(),
  filterByRegion: z.boolean().default(false).optional(),
}).superRefine((arg, ctx) => {
  if (arg.price_rule?.value === 'fixed') {
    arg.contract_product_category_product_prices?.forEach((price, index) => {
      if (!price.value || !unFormatCurrency(price.value)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Campo obrigatório',
          path: ['contract_product_category_product_prices', index, 'value'],
        });
      }
    });

    if (arg.filterByRegion && !!arg.contract_product_category_product_prices?.length) {
      arg.contract_product_category_product_prices?.forEach((price, index) => {
        if (!price.geo) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Campo obrigatório',
            path: ['contract_product_category_product_prices', index, 'geo'],
          });
        }
      });
    }


    if (arg.filterByRegion && !arg.contract_product_category_product_prices?.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Adicione pelo menos um preço por região',
        path: ['price_rule'],
      });
    }
  }
});

export type PublicCompanyLeadsFunnelUpdateContractProductValidationSchema = z.infer<typeof contractProductSchema>;

export const contractProductCategorySchema = z.object({
  id: z.string().optional(),
  require_product_credit_quota: z.boolean(),
  product_category: z.custom<ProductCategoryData>(),
  global_value_in_unit_for_consumption: z.string().optional(),
  global_value_in_currency: z.string().optional(),
  contract_product_category_products: z.array(contractProductSchema).optional().nullable(),
}).superRefine((arg, ctx) => {
  if (arg.require_product_credit_quota) {
    if (!arg.global_value_in_currency || !unFormatCurrency(arg.global_value_in_currency)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Campo obrigatório',
        path: ['global_value_in_currency'],
      });
    }

    if (!arg.global_value_in_unit_for_consumption || !unFormatCurrency(arg.global_value_in_unit_for_consumption)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Campo obrigatório',
        path: ['global_value_in_unit_for_consumption'],
      });
    }

    if (arg.contract_product_category_products?.length) {
      const productsGlobalValueCurrencySum = arg.contract_product_category_products?.reduce((sum, product) => sum + unFormatCurrency(product.global_value_in_currency), 0);
      const productsGlobalValueUnitSum = arg.contract_product_category_products?.reduce((sum, product) => sum + unFormatUnknownUnit(product.global_value_in_unit_for_consumption), 0);

      if (productsGlobalValueCurrencySum < unFormatCurrency(arg.global_value_in_currency || '')) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'O valor da soma da cota total dos produtos é menor que o valor da cota total da categoria',
          path: ['global_value_in_currency'],
        });
      }

      if (productsGlobalValueUnitSum < unFormatUnknownUnit(arg.global_value_in_unit_for_consumption || '')) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'O valor da soma da cota total dos produtos é menor que o valor da cota total da categoria',
          path: ['global_value_in_unit_for_consumption'],
        });
      }

      if (productsGlobalValueCurrencySum > unFormatCurrency(arg.global_value_in_currency || '')) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'O valor da soma da cota total dos produtos é maior que o valor da cota total da categoria',
          path: ['global_value_in_currency'],
        });
      }

      if (productsGlobalValueUnitSum > unFormatUnknownUnit(arg.global_value_in_unit_for_consumption || '')) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'O valor da soma da cota total dos produtos é maior que o valor da cota total da categoria',
          path: ['global_value_in_unit_for_consumption'],
        });
      }
    }
  }
});

export type PublicCompanyLeadsFunnelContractProductCategoryValidationSchema = z.infer<typeof contractProductCategorySchema>;

export const updateContractSchema = z.object({
  contract: z.object({
    duration_in_months: optionSchema.optional().nullable(),
    bill_by: optionSchema.optional().nullable(),
    bill_grouping_mode: optionSchema.optional().nullable(),
    cycle: optionSchema.optional().nullable(),
    cycle_start_day: optionSchema.optional().nullable(),
    cycle_fixed_interval: z.string().optional(),
    closing_to_due_in_days: optionSchema.optional().nullable(),
    contract_equipment_categories: z.array(
      z.object({
        id: z.string(),
        quantity: z.string(),
      })
    ).optional().nullable(),
    contract_fees: z.array(
      z.object({
        id: z.string().optional().nullable(),
        value: z.string().optional().nullable(),
        main_fee: z.boolean().optional().nullable(),
      }).optional().nullable()
    ).optional().nullable(),
    global_value: z.string().optional().nullable(),
    due_to_suspension_in_days: optionSchema.optional().nullable(),
    file: z.object({
      content_type: z.string().optional().nullable(),
      url: z.string().optional().nullable(),
    }).optional().nullable(),
    fiscal_document_requirement: z.object({
      value: z.string().optional().nullable(),
    }).optional().nullable(),
    geo_geoables: z.array(geosSchema).optional(),
    in_effect_since: z.string().optional().nullable(),
    in_effect_until: z.string().optional().nullable(),
    payment_method: optionSchema.optional().nullable(),
    payment_modality: optionSchema.optional().nullable(),
    fiscal_document_consumer: optionSchema.optional().nullable(),
    cnpj_for_fiscal_document: optionSchema.optional().nullable(),
    require_product_credit_quota: z.boolean().optional().nullable(),
    default_fuel_price_rule: optionSchema.optional().nullable(),
  })
});

export type PublicCompanyLeadsFunnelUpdateContractValidationSchema = z.infer<typeof updateContractSchema>;
